import { Entity } from './Entity';
import { ENEMY_TYPES, MovementPattern, ENEMY_PATTERNS } from '../config/enemies';

// Enemy type sprites
const ENEMY_SPRITES: Record<string, HTMLImageElement> = {};
let spritesLoaded = false;

// Load enemy sprites
function loadEnemySprites() {
  if (spritesLoaded) return;
  
  const spriteMap = {
    'hunger_bug': 'assets/hunger-enemy.png',
    'shadow': 'assets/homeless-enemy.png',
    'greed': 'assets/greed-enemy.png',
  };
  
  for (const [type, path] of Object.entries(spriteMap)) {
    const img = new Image();
    img.src = path;
    ENEMY_SPRITES[type] = img;
  }
  
  spritesLoaded = true;
}

// Initialize sprites
loadEnemySprites();

export class Enemy implements Entity {
  public x: number;
  public y: number;
  public width: number;
  public height: number;
  public active: boolean = true;

  private hp: number;
  private maxHp: number;
  private score: number;
  private color: string;
  private type: string;
  private pattern: MovementPattern;
  
  // Movement state - using dx/dy velocity like original
  private moveTimer: number = 0;
  private startX: number;
  private dx: number = 0; // Horizontal velocity
  private dy: number = 0; // Vertical velocity

  constructor(x: number, y: number, type: string) {
    // Find config by matching the type field (not the key)
    let config: any = null;
    for (const key in ENEMY_TYPES) {
      if (ENEMY_TYPES[key].type === type) {
        config = ENEMY_TYPES[key];
        break;
      }
    }
    
    if (!config) {
      console.error('Looking for type:', type);
      console.error('Available enemy types:', Object.values(ENEMY_TYPES).map(e => e.type));
      throw new Error(`Unknown enemy type: ${type}`);
    }

    this.x = x;
    this.y = y;
    this.startX = x;
    this.width = config.size;
    this.height = config.size;
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.score = config.score;
    this.color = config.color;
    this.type = config.type;
    this.pattern = ENEMY_PATTERNS[config.type] || MovementPattern.DRIFT;
    
    // Initialize velocity based on spawn position (like original)
    if (x < 0) {
      // Left side spawn
      this.dx = 0.5 + Math.random() * 1;
      this.dy = Math.random() * 1;
    } else if (x > 480) {
      // Right side spawn
      this.dx = -(0.5 + Math.random() * 1);
      this.dy = Math.random() * 1;
    } else {
      // Top spawn (most common)
      this.dx = (Math.random() - 0.5) * 1.5; // -0.75 to 0.75 horizontal
      this.dy = 0.8 + Math.random() * 1.2; // 0.8 to 2.0 downward (slower)
    }
  }

  public update(deltaTime: number): void {
    this.moveTimer += deltaTime;

    // Original game-style velocity-based movement
    // Apply pattern-based modifications to velocity
    let horizontalMod = 0;
    let verticalMod = 1;
    
    switch (this.pattern) {
      case MovementPattern.ZIGZAG:
        horizontalMod = Math.sin(this.moveTimer * 5) * 2;
        verticalMod = 1.2;
        break;
      case MovementPattern.DRIFT:
        horizontalMod = Math.cos(this.moveTimer * 2) * 1.5;
        break;
      case MovementPattern.DIVE:
        verticalMod = 1 + this.moveTimer * 0.8;
        break;
      case MovementPattern.HORIZONTAL:
        horizontalMod = Math.sin(this.moveTimer * 3) * 3;
        verticalMod = 0.8;
        break;
      case MovementPattern.SINE_WAVE:
        this.x = this.startX + Math.sin(this.y / 30) * 80;
        break;
    }
    
    // Update position using velocity (like original game)
    this.x += (this.dx + horizontalMod);
    this.y += this.dy * verticalMod;
    
    // Bounce off horizontal edges (like original)
    if (this.x <= 0 || this.x >= 480 - this.width) {
      this.dx *= -1;
      this.startX = this.x;
    }

    // Remove if off screen (like original - larger bounds)
    if (this.y > 960 + 100) {
      this.active = false;
    }
  }

  public takeDamage(amount: number): boolean {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.active = false;
      return true; // Enemy defeated
    }
    return false;
  }
  
  // Get enemy type for split behavior and special effects
  public getType(): string {
    return this.type;
  }
  
  // Get enemy score value
  public getScore(): number {
    return this.score;
  }
  
  // Get position for spawning split enemies
  public getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  public render(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    // Try to render enemy sprite if loaded
    const sprite = ENEMY_SPRITES[this.type];
    if (sprite && sprite.complete && sprite.naturalWidth > 0) {
      // Image loaded successfully
      ctx.drawImage(
        sprite,
        this.x - this.width / 2,
        this.y - this.height / 2,
        this.width,
        this.height
      );
    } else {
      // Fallback to procedural rendering
      ctx.translate(this.x, this.y);
      ctx.fillStyle = this.color;
      
      switch (this.type) {
        case 'hunger_bug':
          this.renderHungerBug(ctx);
          break;
        case 'shadow':
          this.renderShadow(ctx);
          break;
        case 'greed':
          this.renderGreed(ctx);
          break;
        case 'cloud':
          this.renderCloud(ctx);
          break;
        default:
          ctx.beginPath();
          ctx.arc(0, 0, this.width / 2, 0, Math.PI * 2);
          ctx.fill();
      }
      ctx.translate(-this.x, -this.y);
    }

    // HP bar if damaged
    if (this.hp < this.maxHp) {
      const barWidth = this.width;
      const barHeight = 4;
      const hpPercent = this.hp / this.maxHp;
      
      ctx.fillStyle = '#ff0000';
      ctx.fillRect(this.x - barWidth / 2, this.y - this.height / 2 - 8, barWidth, barHeight);
      
      ctx.fillStyle = '#00ff00';
      ctx.fillRect(this.x - barWidth / 2, this.y - this.height / 2 - 8, barWidth * hpPercent, barHeight);
    }

    ctx.restore();
  }

  private renderHungerBug(ctx: CanvasRenderingContext2D): void {
    // Bug-like enemy
    ctx.beginPath();
    ctx.ellipse(0, 0, this.width / 2, this.height / 2.5, 0, 0, Math.PI * 2);
    ctx.fill();
    
    // Antennae
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-5, -this.height / 3);
    ctx.lineTo(-10, -this.height / 2);
    ctx.moveTo(5, -this.height / 3);
    ctx.lineTo(10, -this.height / 2);
    ctx.stroke();
  }

  private renderShadow(ctx: CanvasRenderingContext2D): void {
    // Shadowy figure with fade effect
    const alpha = 0.3 + Math.abs(Math.sin(this.moveTimer * 3)) * 0.5;
    ctx.globalAlpha = alpha;
    ctx.beginPath();
    ctx.arc(0, 0, this.width / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  private renderGreed(ctx: CanvasRenderingContext2D): void {
    // Gremlin with $ sign
    ctx.beginPath();
    ctx.arc(0, 0, this.width / 2, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#ffff00';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('$', 0, 0);
  }

  private renderCloud(ctx: CanvasRenderingContext2D): void {
    // Cloud shape
    ctx.beginPath();
    ctx.arc(-this.width / 4, 0, this.width / 3, 0, Math.PI * 2);
    ctx.arc(this.width / 4, 0, this.width / 3, 0, Math.PI * 2);
    ctx.arc(0, -this.height / 4, this.width / 3, 0, Math.PI * 2);
    ctx.fill();
  }

  public getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.x - this.width / 2,
      y: this.y - this.height / 2,
      width: this.width,
      height: this.height,
    };
  }
  
  public getName(): string {
    return ENEMY_TYPES[this.type.toUpperCase().replace('_', '')]?.name || this.type;
  }

  // Set enemy velocity for shield bounce effects
  public setVelocity(dx: number, dy: number): void {
    this.dx = dx;
    this.dy = dy;
  }

  // Get current velocity
  public getVelocity(): { dx: number; dy: number } {
    return { dx: this.dx, dy: this.dy };
  }
}
